import * as React from 'react';
import { AppContext } from '../../../App';
import { useQueryClient } from '@tanstack/react-query';
import { CircularProgress, DialogContentText } from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

import { useTrainingSetResetValidationRules } from '../../hooks/useTrainingSetResetValidationRules';

import IconButtonWhiteBg from '../../common/IconButtonWhiteBg';
import DialogGlynt from '../../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../../common/Dialog/DialogActionsGlynt';

export interface ITrainingSetResetValidationRulesProps {
  trainingSetId: string;
}

const TrainingSetResetValidationRules: React.FC<ITrainingSetResetValidationRulesProps> = ({
  trainingSetId,
}) => {
  const { setToast } = React.useContext(AppContext);
  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleCloseConfirmation = () => {
    setOpen(false);
  };

  const client = useQueryClient();

  const resetValidationRules = useTrainingSetResetValidationRules({
    onSuccess: () => {
      client.invalidateQueries({
        queryKey: ['trainingSets']
      });
      client.invalidateQueries({
        queryKey: ['trainingSet', trainingSetId]
      });
      setOpen(false);

      setToast({
        variant: 'success',
        open: true,
        message: `Validation rules reset successfully`,
      });
    },
    onError: (error) => {
      setToast({
        variant: 'error',
        open: true,
        message: `Sorry, failed to reset validation rules. ${error}`,
      });
    },
  });

  return (
    <>
      <IconButtonWhiteBg
        disabled={resetValidationRules.isPending}
        onClick={handleClickOpen}
        id="icon-training-set-reset-validation-rules"
        cypressId="icon-training-set-reset-validation-rules"
        tooltip="Reset Validation Rules"
      >
        {resetValidationRules.isPending ? (
          <CircularProgress size={14} />
        ) : (
          <RestartAltIcon style={{ fontSize: '14px', color: 'black' }} />
        )}
      </IconButtonWhiteBg>

      <DialogGlynt
        open={open}
        closeModal={handleCloseConfirmation}
        loading={resetValidationRules.isPending}
        cypressId="dialog-training-set-reset-validation-rules"
      >
        <DialogTitleGlynt
          title="Confirmation"
          closeModal={handleCloseConfirmation}
          loading={resetValidationRules.isPending}
        />
        <DialogContentGlynt>
          <DialogContentText>
            Are you sure you want to reset the validation rules for this training set?
          </DialogContentText>
        </DialogContentGlynt>
        <DialogActionsGlynt
          handleSubmit={() => resetValidationRules.mutate({ trainingSetId })}
          closeModal={handleCloseConfirmation}
          cancelCypressId="btn-training-set-reset-validation-rules-modal-cancel"
          submitCypressId="btn-training-set-reset-validation-rules-modal-yes"
          clearCypressId=""
          submitButtonText="Yes"
          submitLoading={resetValidationRules.isPending}
          showClearButton={false}
        />
      </DialogGlynt>
    </>
  );
};

export default TrainingSetResetValidationRules;
